import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Card, Empty, Loading, Table } from "@/components/Ui";

export default function SupplierInvoices() {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/supplier/invoices").then((r) => setRows(r.data)).catch(() => setRows([]));
  }, []);

  const download = async (row) => {
    setBusy(row.session_id);
    try {
      const res = await api.get(`/supplier/invoices/${row.session_id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${row.invoice_number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  if (!rows) return <Loading label="Loading invoices…" />;

  return (
    <div data-testid="supplier-invoices">
      <Card>
        <h2 className="font-display text-2xl font-black tracking-tight text-[#102A43]">Tax invoices</h2>
        <p className="mt-2 max-w-2xl text-sm text-[#667788]">
          Every paid subscription generates a UAE tax invoice with our TRN and the 5% VAT breakdown,
          ready for your accounts team.
        </p>
      </Card>
      {rows.length === 0 ? (
        <Empty testId="invoices-empty" title="No invoices yet"
          sub="Invoices appear here as soon as a subscription payment is completed." />
      ) : (
        <div className="mt-6">
          <Table testId="invoices-table"
            head={["Invoice", "Plan", "Date", "Subtotal", "VAT", "Total", ""]}>
            {rows.map((r) => (
              <tr key={r.session_id} data-testid={`invoice-row-${r.session_id}`}>
                <td className="font-semibold text-[#102A43]">{r.invoice_number}</td>
                <td className="capitalize">{r.plan}</td>
                <td>{r.paid_at ? new Date(r.paid_at).toLocaleDateString() : "—"}</td>
                <td>{r.currency} {r.subtotal?.toLocaleString()}</td>
                <td>{r.currency} {r.tax?.toLocaleString()}</td>
                <td className="font-bold text-[#102A43]">{r.currency} {r.total?.toLocaleString()}</td>
                <td>
                  <button data-testid={`download-invoice-${r.session_id}`} onClick={() => download(r)}
                    disabled={busy === r.session_id}
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-[#147D78] underline disabled:opacity-50">
                    <Download className="h-3.5 w-3.5" /> PDF
                  </button>
                </td>
              </tr>
            ))}
          </Table>
        </div>
      )}
    </div>
  );
}
